import { useState, useEffect } from 'react';
import { Star, ThumbsUp, User } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Star Rating Component
export function StarRating({ rating, onRatingChange, size = 'md', readonly = false }) {
  const [hoverRating, setHoverRating] = useState(0);
  
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8'
  };

  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={readonly}
          className={`${readonly ? 'cursor-default' : 'cursor-pointer'} transition-colors`}
          onMouseEnter={() => !readonly && setHoverRating(star)}
          onMouseLeave={() => !readonly && setHoverRating(0)}
          onClick={() => !readonly && onRatingChange && onRatingChange(star)}
        >
          <Star
            className={`${sizeClasses[size]} ${
              star <= (hoverRating || rating)
                ? 'fill-yellow-400 text-yellow-400'
                : 'text-slate-300'
            }`}
          />
        </button>
      ))}
    </div>
  );
}

// Average Rating Display
export function RatingDisplay({ rating, totalReviews, size = 'md' }) {
  return (
    <div className="flex items-center gap-2">
      <StarRating rating={Math.round(rating)} readonly size={size} />
      <span className="font-semibold text-slate-900">{rating?.toFixed(1) || '0.0'}</span>
      {totalReviews !== undefined && (
        <span className="text-slate-500">({totalReviews} reviews)</span>
      )}
    </div>
  );
}

// Review Card
export function ReviewCard({ review, onDelete, canDelete = false }) {
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="border border-slate-200 rounded-xl p-4 bg-white" data-testid={`review-${review.id}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
            <User className="w-5 h-5 text-orange-600" />
          </div>
          <div>
            <p className="font-medium text-slate-900">{review.user_name || 'Customer'}</p>
            <p className="text-sm text-slate-500">{formatDate(review.created_at)}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <StarRating rating={review.rating} readonly size="sm" />
          {canDelete && (
            <Button
              variant="ghost"
              size="sm"
              className="text-red-500 hover:text-red-700"
              onClick={() => onDelete(review.id)}
            >
              Delete
            </Button>
          )}
        </div>
      </div>
      
      {review.review_text && (
        <p className="text-slate-600 mb-3">{review.review_text}</p>
      )}
      
      {(review.food_rating || review.delivery_rating) && (
        <div className="flex gap-4 text-sm text-slate-500">
          {review.food_rating && (
            <span>Food: <StarRating rating={review.food_rating} readonly size="sm" /></span>
          )}
          {review.delivery_rating && (
            <span>Delivery: <StarRating rating={review.delivery_rating} readonly size="sm" /></span>
          )}
        </div>
      )}
    </div>
  );
}

// Reviews List for Restaurant
export function RestaurantReviews({ restaurantId }) {
  const [reviews, setReviews] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReviews();
  }, [restaurantId]);

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/reviews/restaurant/${restaurantId}`);
      setReviews(response.data.reviews);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Failed to fetch reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-white rounded-xl p-4 animate-pulse">
            <div className="h-6 bg-slate-200 rounded w-1/4 mb-2"></div>
            <div className="h-4 bg-slate-200 rounded w-3/4"></div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-slate-900">Customer Reviews ({total})</h3>
      </div>
      
      {reviews.length > 0 ? (
        <div className="space-y-4">
          {reviews.map((review) => (
            <ReviewCard key={review.id} review={review} />
          ))}
        </div>
      ) : (
        <div className="text-center py-8 bg-slate-50 rounded-xl">
          <Star className="w-12 h-12 mx-auto text-slate-300 mb-2" />
          <p className="text-slate-500">No reviews yet</p>
        </div>
      )}
    </div>
  );
}

// Write Review Dialog
export function WriteReviewDialog({ orderId, restaurantId, restaurantName, onReviewSubmitted }) {
  const [isOpen, setIsOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [foodRating, setFoodRating] = useState(0);
  const [deliveryRating, setDeliveryRating] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (rating === 0) {
      toast.error('Please select a rating');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/reviews`,
        {
          restaurant_id: restaurantId,
          order_id: orderId,
          rating,
          food_rating: foodRating || null,
          delivery_rating: deliveryRating || null,
          review_text: reviewText,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Review submitted!');
      setIsOpen(false);
      resetForm();
      if (onReviewSubmitted) onReviewSubmitted();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setRating(0);
    setFoodRating(0);
    setDeliveryRating(0);
    setReviewText('');
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="gap-2" data-testid="write-review-btn">
          <Star className="w-4 h-4" />
          Write a Review
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Review {restaurantName}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Overall Rating */}
          <div>
            <Label className="mb-2 block">Overall Rating *</Label>
            <StarRating rating={rating} onRatingChange={setRating} size="lg" />
          </div>

          {/* Food Rating */}
          <div>
            <Label className="mb-2 block">Food Quality</Label>
            <StarRating rating={foodRating} onRatingChange={setFoodRating} size="md" />
          </div>

          {/* Delivery Rating */}
          <div>
            <Label className="mb-2 block">Delivery Experience</Label>
            <StarRating rating={deliveryRating} onRatingChange={setDeliveryRating} size="md" />
          </div>

          {/* Review Text */}
          <div>
            <Label htmlFor="reviewText">Your Review</Label>
            <Textarea
              id="reviewText"
              value={reviewText}
              onChange={(e) => setReviewText(e.target.value)}
              placeholder="Tell us about your experience..."
              rows={4}
              className="mt-2"
              data-testid="review-text"
            />
          </div>

          <div className="flex gap-2 justify-end">
            <Button type="button" variant="outline" onClick={() => setIsOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" className="btn-primary" disabled={loading} data-testid="submit-review">
              {loading ? 'Submitting...' : 'Submit Review'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default RestaurantReviews;
